import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchOrderHistory,
  fetchServiceHistory,
  reorderItems,
} from '../../api/clientHistory';
import { submitReview, updateReview } from '../../api/reviews';
import InvoiceActions from '../../components/invoices/InvoiceActions';
import OrderStatusStepper from '../../components/orders/OrderStatusStepper';
import { OrderPricingBreakdown } from '../../components/pricing/OrderPricingManager';
import { itemLineTotal } from '../../utils/pricingDisplay';
import ClientReviewSummary from '../../components/reviews/ClientReviewSummary';
import ReviewModal from '../../components/reviews/ReviewModal';
import DashboardShell from '../../components/ui/DashboardShell';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';

const OrderReceiptModal = ({ order, onClose, t }) => {
  if (!order) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <h2 className="text-lg font-semibold">{t('orders.receiptTitle')}</h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-500">
          {t('orders.drawer.orderNumber', { number: order._id.slice(-8).toUpperCase() })} · {order.salonName}
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          {order.items?.map((item, i) => (
            <li key={i} className="flex justify-between">
              <span>{item.productName} × {item.quantity}</span>
              <span>{t('common.currency', { amount: itemLineTotal(item).toLocaleString() })}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4">
          <OrderPricingBreakdown order={order} />
        </div>
        <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-500">{order.shippingAddress?.street}, {order.shippingAddress?.city}</p>
        {order.status === 'DELIVERED' && (
          <div className="mt-4 border-t border-zinc-200 dark:border-zinc-800 pt-4">
            <p className="mb-2 text-sm font-medium">{t('invoice.title')}</p>
            <InvoiceActions kind="order" entityId={order._id} />
          </div>
        )}
        <button type="button" onClick={onClose} className="mt-6 w-full rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm">{t('common.close')}</button>
      </div>
    </>
  );
};

const ClientHistoryPage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('services');
  const [reviewTarget, setReviewTarget] = useState(null);
  const [editReview, setEditReview] = useState(null);
  const [receiptOrder, setReceiptOrder] = useState(null);
  const { showToast } = useToast();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: serviceData, isLoading: loadingServices } = useQuery({
    queryKey: ['client-service-history'],
    queryFn: fetchServiceHistory,
    enabled: tab === 'services',
  });

  const { data: orderData, isLoading: loadingOrders } = useQuery({
    queryKey: ['client-order-history'],
    queryFn: fetchOrderHistory,
    enabled: tab === 'orders',
  });

  const reviewMut = useMutation({
    mutationFn: submitReview,
    onSuccess: () => {
      showToast(t('toast.reviewSubmitted'), 'success');
      setReviewTarget(null);
      queryClient.invalidateQueries({ queryKey: ['client-service-history'] });
      queryClient.invalidateQueries({ queryKey: ['client-order-history'] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const updateReviewMut = useMutation({
    mutationFn: ({ id, body }) => updateReview(id, body),
    onSuccess: () => {
      showToast(t('toast.reviewUpdated'), 'success');
      setEditReview(null);
      queryClient.invalidateQueries({ queryKey: ['client-service-history'] });
      queryClient.invalidateQueries({ queryKey: ['client-order-history'] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const reorderMut = useMutation({
    mutationFn: reorderItems,
    onSuccess: (res) => {
      res.cartItems?.forEach((item) => {
        addItem(
          {
            _id: item.productId,
            tenantId: item.tenantId,
            branchId: item.branchId,
            name: item.name,
            price: item.price,
            images: item.image ? [{ url: item.image }] : [],
            stock: item.stock,
          },
          item.quantity
        );
      });
      showToast(t('toast.itemsAddedToCart'), 'success');
      navigate('/client/cart');
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.reorderFailed'), 'error'),
  });

  const appointments = serviceData?.appointments || [];
  const orders = orderData?.orders || [];

  const tabs = [
    { id: 'services', label: t('client.history.serviceHistory') },
    { id: 'orders', label: t('client.history.orderHistory') },
  ];

  return (
    <DashboardShell title={t('client.history.myHistory')} subtitle={t('client.history.subtitle')}>
      <div className="mb-6 flex gap-2">
        {tabs.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            onClick={() => setTab(tabItem.id)}
            className={`rounded-lg px-4 py-2 text-sm ${tab === tabItem.id ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'}`}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {tab === 'services' && (
        <>
          {loadingServices && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
          <div className="space-y-4">
            {appointments.map((a) => (
              <div key={a._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{a.serviceName}</p>
                    <p className="text-sm text-zinc-600 dark:text-zinc-500">{a.salonName} · {a.branchName}</p>
                    <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">
                      {new Date(a.confirmedDate || a.preferredDate).toLocaleDateString()}
                      {a.staffName && ` · ${a.staffName}`}
                    </p>
                  </div>
                  <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-3 py-1 text-xs">{t(`status.appointment.${a.status}`)}</span>
                </div>
                {a.status === 'COMPLETED' && !a.review && (
                  <button
                    type="button"
                    onClick={() => setReviewTarget(a)}
                    className="mt-3 rounded-lg border border-amber-700/50 px-3 py-1.5 text-xs text-amber-300"
                  >
                    {t('client.history.leaveReviewStar')}
                  </button>
                )}
                {a.review && (
                  <ClientReviewSummary
                    review={a.review}
                    onEdit={() =>
                      setEditReview({ ...a.review, serviceName: a.serviceName, salonName: a.salonName })
                    }
                  />
                )}
                {a.status === 'COMPLETED' && (
                  <div className="mt-4 border-t border-zinc-200 dark:border-zinc-800 pt-4">
                    <InvoiceActions kind="appointment" entityId={a._id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'orders' && (
        <>
          {loadingOrders && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{t('orders.drawer.orderNumber', { number: order._id.slice(-8).toUpperCase() })}</p>
                    <p className="text-sm text-zinc-600 dark:text-zinc-500">{order.salonName}</p>
                    <p className="text-xs text-zinc-600 dark:text-zinc-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                  </div>
                  <p className="font-semibold text-glow-300">{t('common.currency', { amount: order.totalAmount?.toLocaleString() })}</p>
                </div>
                <div className="mt-2 flex gap-1">
                  {order.items?.slice(0, 4).map((item, i) => (
                    <div key={i} className="h-10 w-10 rounded bg-zinc-100 dark:bg-zinc-800 text-center text-[9px] leading-10 text-zinc-600 dark:text-zinc-500">
                      {item.productName?.slice(0, 6)}
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <OrderStatusStepper status={order.status} />
                </div>
                {order.status === 'DELIVERED' && !order.review && (
                  <button
                    type="button"
                    onClick={() => setReviewTarget({ kind: 'PRODUCT', order })}
                    className="mt-3 rounded-lg border border-amber-700/50 px-3 py-1.5 text-xs text-amber-300"
                  >
                    {t('reviews.reviewOrder')}
                  </button>
                )}
                {order.review && (
                  <ClientReviewSummary
                    review={order.review}
                    onEdit={() =>
                      setEditReview({ ...order.review, salonName: order.salonName })
                    }
                  />
                )}
                <div className="mt-4 flex gap-3">
                  <button type="button" onClick={() => reorderMut.mutate(order._id)} className="text-xs text-glow-400 hover:underline">
                    {t('client.history.reorder')}
                  </button>
                  {order.status === 'DELIVERED' && (
                    <button type="button" onClick={() => setReceiptOrder(order)} className="text-xs text-zinc-600 dark:text-zinc-400 hover:underline">
                      {t('invoice.view')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ReviewModal
        open={!!reviewTarget}
        title={
          reviewTarget?.kind === 'PRODUCT' ? t('reviews.reviewOrder') : t('reviews.reviewService')
        }
        subtitle={
          reviewTarget?.kind === 'PRODUCT'
            ? t('reviews.orderAtSalon', { salon: reviewTarget.order?.salonName })
            : reviewTarget
              ? t('reviews.serviceAtSalon', {
                  service: reviewTarget.serviceName,
                  salon: reviewTarget.salonName,
                })
              : ''
        }
        onClose={() => setReviewTarget(null)}
        saving={reviewMut.isPending}
        onSubmit={({ rating, comment }) => {
          if (reviewTarget?.kind === 'PRODUCT') {
            const order = reviewTarget.order;
            reviewMut.mutate({
              type: 'PRODUCT',
              tenantId: order.tenantId?._id || order.tenantId,
              orderId: order._id,
              rating,
              comment,
            });
          } else {
            reviewMut.mutate({
              type: 'SERVICE',
              tenantId: reviewTarget.tenantId?._id || reviewTarget.tenantId,
              branchId: reviewTarget.branchId?._id || reviewTarget.branchId,
              appointmentId: reviewTarget._id,
              rating,
              comment,
            });
          }
        }}
      />

      <ReviewModal
        open={!!editReview}
        title={t('reviews.editReview')}
        subtitle={editReview?.contextLabel || ''}
        initialRating={editReview?.rating || 0}
        initialComment={editReview?.comment || ''}
        submitLabel={t('common.save')}
        onClose={() => setEditReview(null)}
        saving={updateReviewMut.isPending}
        onSubmit={({ rating, comment }) =>
          updateReviewMut.mutate({ id: editReview._id, body: { rating, comment } })
        }
      />

      <OrderReceiptModal order={receiptOrder} onClose={() => setReceiptOrder(null)} t={t} />
    </DashboardShell>
  );
};

export default ClientHistoryPage;
